import { useEffect, useState } from "react";

import { Dialog } from "../base/Dialog";
import { getTelephonyProviderInfo } from "../../utils/telephony";
import { Icon } from "../base/Icon";
import { PhoneNumber } from "../../redux/reducers/userSlice";
import { TelephonyConfigurationDropdown } from "./TelephonyConfigurationDropdown";
import { useApiRequest } from "../../hooks/useApiRequest";
import { useAppSelector } from "../../redux";
import { useEffectiveOrganization } from "../../hooks/useEffectiveOrganization";
import { View, Text, SettingsList, SettingsListItem } from "../base";
import { OrganizationSettingsTab } from "./OrganizationSettingTab";

type PhoneNumberCardProps = {
  phoneNumberId?: string;
  className?: string;
};

export const PhoneNumberCard = ({ phoneNumberId, className }: PhoneNumberCardProps) => {
  const [showProviderDialog, setShowProviderDialog] = useState(false);
  const [selectedTelephonyId, setSelectedTelephonyId] = useState("");
  const getPhoneNumberApiRequest = useApiRequest<{ data: PhoneNumber }>();
  const createPhoneNumberApiRequest = useApiRequest<{ data: PhoneNumber }>();

  // Get telephony configurations from Redux store
  const { organizationId: effectiveOrganizationId } = useEffectiveOrganization();
  const telephonyConfigs = useAppSelector((state) =>
    effectiveOrganizationId ? Object.values(state.organizationTelephony.entities).filter((config) => config.organizationId === effectiveOrganizationId) : [],
  );

  const handleIssuePhoneNumber = async () => {
    try {
      await createPhoneNumberApiRequest.fetch("/v1/users/phoneNumber", {
        method: "POST",
        body: {
          telephonyId: selectedTelephonyId,
        },
      });

      await getPhoneNumberApiRequest.fetch("/v1/users/phoneNumber");

      setShowProviderDialog(false);
      setSelectedTelephonyId("");
    } catch (error) {
      console.error("Failed to issue phone number:", error);
    }
  };

  useEffect(() => {
    getPhoneNumberApiRequest.fetch("/v1/users/phoneNumber");
  }, []);

  const phoneNumber = getPhoneNumberApiRequest.data?.data.phoneNumber;
  const phoneNumberData = getPhoneNumberApiRequest.data?.data;

  // Find the telephony configuration for the current phone number
  const telephonyConfig = phoneNumberData?.telephonyId ? telephonyConfigs.find((config) => config.id === phoneNumberData.telephonyId) : null;
  const providerInfo = telephonyConfig ? getTelephonyProviderInfo(telephonyConfig.providerType) : getTelephonyProviderInfo("");

  return (
    <OrganizationSettingsTab
      title="Phone Number"
      count={phoneNumber ? 1 : 0}
      className={className}
    >
      <SettingsList>
        {phoneNumber && (
          <SettingsListItem
            title={phoneNumber}
            subtitle={telephonyConfig ? telephonyConfig.name : providerInfo.name}
            icon="phone"
          />
        )}
        {(!phoneNumberId || !phoneNumber) && (
          <button
            onClick={() => setShowProviderDialog(true)}
            className="flex flex-row items-center gap-2 px-4 py-3 w-full text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
          >
            <View className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 group-hover:border-brand dark:group-hover:border-brand-light transition-colors">
              <Icon
                name="add"
                size={14}
                className="text-gray-400 dark:text-gray-500 group-hover:text-brand dark:group-hover:text-brand-light transition-colors"
              />
            </View>
            <Text className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
              Add phone number
            </Text>
          </button>
        )}
      </SettingsList>

      <Dialog
        isOpen={showProviderDialog}
        onClose={() => {
          setShowProviderDialog(false);
          setSelectedTelephonyId("");
        }}
        title="Issue Phone Number"
      >
        <View className="flex flex-col gap-6 p-6">
          <View className="flex flex-col gap-2">
            <Text
              variant="muted"
              className="text-sm"
            >
              Select a telephony configuration to issue a phone number. You can choose VoiceRun or one of your configured providers.
            </Text>
          </View>

          <TelephonyConfigurationDropdown
            value={selectedTelephonyId}
            onChange={setSelectedTelephonyId}
            organizationId={effectiveOrganizationId}
            placeholder="Select a telephony configuration"
          />

          <View className="flex flex-row gap-3 mt-2">
            <button
              onClick={handleIssuePhoneNumber}
              disabled={createPhoneNumberApiRequest.isLoading}
              className="flex-1 flex flex-row items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 disabled:opacity-50"
            >
              {createPhoneNumberApiRequest.isLoading ? (
                <Icon
                  name="loader"
                  size={14}
                  className="animate-spin"
                />
              ) : (
                <Icon
                  name="add"
                  size={14}
                />
              )}
              Issue Number
            </button>
            <button
              onClick={() => {
                setShowProviderDialog(false);
                setSelectedTelephonyId("");
              }}
              className="flex-1 flex flex-row items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </View>
        </View>
      </Dialog>
    </OrganizationSettingsTab>
  );
};
